import { EditorView } from "@codemirror/view";
import { HighlightStyle, syntaxHighlighting } from "@codemirror/language";
import { tags } from "@lezer/highlight";

export const liliaEditorTheme = EditorView.theme({
  "&": {
    backgroundColor: "var(--lilia-code-bg)",
    color: "var(--lilia-code-text)",
    fontSize: "15px",
    fontFamily: "var(--lilia-font-mono)",
  },
  ".cm-content": {
    caretColor: "var(--lilia-code-cursor)",
    padding: "16px 12px",
    lineHeight: "1.65",
  },
  ".cm-line": {
    padding: "0 4px",
  },
  ".cm-gutters": {
    backgroundColor: "var(--lilia-code-gutter-bg)",
    color: "var(--lilia-code-gutter)",
    border: "none",
    borderRight: "1px solid var(--lilia-border)",
    paddingRight: "8px",
  },
  ".cm-activeLineGutter": {
    backgroundColor: "var(--lilia-code-active-line)",
  },
  ".cm-activeLine": {
    backgroundColor: "var(--lilia-code-active-line)",
  },
  "&.cm-focused .cm-cursor": {
    borderLeftColor: "var(--lilia-code-cursor)",
  },
  "&.cm-focused .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection": {
    backgroundColor: "var(--lilia-code-selection)",
  },
  ".cm-searchMatch": {
    backgroundColor: "rgba(255, 200, 0, 0.3)",
    outline: "1px solid rgba(255, 200, 0, 0.5)",
  },
  ".cm-searchMatch.cm-searchMatch-selected": {
    backgroundColor: "rgba(255, 150, 0, 0.4)",
  },
  ".cm-matchingBracket": {
    backgroundColor: "rgba(100, 150, 255, 0.2)",
    outline: "1px solid rgba(100, 150, 255, 0.5)",
  },
  ".cm-selectionMatch": {
    backgroundColor: "rgba(100, 150, 255, 0.15)",
  },
  ".cm-scroller": {
    overflow: "auto",
  },
});

export const liliaHighlightStyle = HighlightStyle.define([
  // Comments
  { tag: tags.comment, color: "var(--lilia-code-comment)", fontStyle: "italic" },
  { tag: tags.lineComment, color: "var(--lilia-code-comment)", fontStyle: "italic" },
  { tag: tags.blockComment, color: "var(--lilia-code-comment)", fontStyle: "italic" },

  // Keywords & control flow
  { tag: tags.keyword, color: "var(--lilia-code-command)" },
  { tag: tags.controlKeyword, color: "var(--lilia-code-command)", fontWeight: "bold" },
  { tag: tags.operatorKeyword, color: "var(--lilia-code-command)", fontWeight: "bold" },
  { tag: tags.moduleKeyword, color: "var(--lilia-code-command)" },
  { tag: tags.definitionKeyword, color: "var(--lilia-code-command)" },

  // Literals
  { tag: tags.string, color: "var(--lilia-code-string)" },
  { tag: tags.special(tags.string), color: "var(--lilia-code-regexp)" },
  { tag: tags.regexp, color: "var(--lilia-code-regexp)" },
  { tag: tags.number, color: "var(--lilia-code-number)" },
  { tag: tags.integer, color: "var(--lilia-code-number)" },
  { tag: tags.float, color: "var(--lilia-code-number)" },
  { tag: tags.bool, color: "var(--lilia-code-bool)", fontWeight: "bold" },
  { tag: tags.null, color: "var(--lilia-code-bool)" },
  { tag: tags.atom, color: "var(--lilia-code-bool)", fontStyle: "italic" },
  { tag: tags.escape, color: "var(--lilia-code-escape)" },

  // Functions & definitions
  { tag: tags.function(tags.variableName), color: "var(--lilia-code-function)" },
  { tag: tags.function(tags.definition(tags.variableName)), color: "var(--lilia-code-definition)", fontWeight: "bold" },
  { tag: tags.definition(tags.variableName), color: "var(--lilia-code-definition)" },
  { tag: tags.definition(tags.propertyName), color: "var(--lilia-code-definition)" },

  // Types & classes
  { tag: tags.typeName, color: "var(--lilia-code-type)" },
  { tag: tags.className, color: "var(--lilia-code-type)" },
  { tag: tags.namespace, color: "var(--lilia-code-type)" },
  { tag: tags.macroName, color: "var(--lilia-code-type)" },
  { tag: tags.self, color: "var(--lilia-code-self)", fontStyle: "italic" },

  // Variables & properties
  { tag: tags.variableName, color: "var(--lilia-code-variable)" },
  { tag: tags.propertyName, color: "var(--lilia-code-property)" },
  { tag: tags.special(tags.variableName), color: "var(--lilia-code-variable)" },

  // Punctuation & operators
  { tag: tags.brace, color: "var(--lilia-code-brace)" },
  { tag: tags.squareBracket, color: "var(--lilia-code-bracket)" },
  { tag: tags.paren, color: "var(--lilia-code-text)" },
  { tag: tags.separator, color: "var(--lilia-code-separator)" },
  { tag: tags.operator, color: "var(--lilia-code-separator)" },
  { tag: tags.punctuation, color: "var(--lilia-code-text)" },

  // HTML/XML tags & attributes
  { tag: tags.tagName, color: "var(--lilia-code-tag)" },
  { tag: tags.attributeName, color: "var(--lilia-code-attr-key)" },
  { tag: tags.attributeValue, color: "var(--lilia-code-string)" },
  { tag: tags.angleBracket, color: "var(--lilia-code-text)" },

  // LaTeX-specific
  { tag: tags.processingInstruction, color: "var(--lilia-code-mathdelim)" },
  // Body prose between commands (so paired source keeps readable body text)
  { tag: tags.content, color: "var(--lilia-code-text)" },
]);

export const liliaHighlighting = syntaxHighlighting(liliaHighlightStyle);
